import {
  EventLocationData,
  Undefinable
} from '@eagle/react-common';
import { DateTime } from 'luxon';
import { stripTrailingStatusDot, toEventLabel, toEventTime } from './trip-history-event-display-utils';
import {
  filterExcludedTripHistoryEvents,
  getEventLatLng,
  getThingRangeBoundaryEvents,
  getThingRangeMapEvents,
  getTripEventsInRange,
  getVisibleTripTimelineEvents,
  isExcludedTripHistoryEvent,
  isLocationUpdateEvent,
  TripSummaryLike,
} from './trip-history-event-filtering-utils';
import {
  isSameTripJourneyMapItem,
  isTripHistoryMapItem,
} from './trip-history-map-item-utils';
import {
  formatTotalDistanceLabel,
  formatTotalFuelLabel,
  formatTripDistanceLabel,
  formatTripDurationLabel,
  formatTripEngineHoursLabel,
  formatTripFuelEfficiencyLabel,
  formatTripFuelLabel,
  formatTripOdometerLabel,
} from './trip-history-metric-formatters';
import {
  buildTripRouteFullAddressLabel,
  buildTripRouteLabel,
  formatAddressText,
  formatLatLngText,
  formatSuburbOrLatLngText,
  resolveAddressText,
  ReverseGeocodeResolver,
} from './trip-history-route-address-utils';
import {
  buildTripEventSummary,
  enrichEventsWithNearestLocationUpdate,
  filterVisibleTripTimelineEvents,
  getEventColor,
  getEventDisplayLabel,
  getTripTimelineProcessedEvents,
  isVisibleTripTimelineEvent,
  TripEventSummarizationOptions,
  TripEventSummaryItem,
  TripLocationEnrichmentOptions,
  TripTimelineProcessedEvent,
} from './trip-history-timeline-processing-utils';

export const getThingColorMap = <T extends { _id: string }>(
  entities: T[],
  currentColorById: Record<string, string> = {},
  thingColors: string[] = [],
): Record<string, string> => {
  const nextColorById: Record<string, string> = {};
  const fallbackColor = thingColors[0] ?? '#1E75D8';

  entities.forEach((entity, index) => {
    const currentColor = currentColorById[entity._id];
    if (currentColor) {
      nextColorById[entity._id] = currentColor;
      return;
    }

    const usedColors = new Set(Object.values(nextColorById));
    const nextColor = thingColors.find((color) => !usedColors.has(color))
      ?? thingColors[index % (thingColors.length || 1)]
      ?? fallbackColor;
    nextColorById[entity._id] = nextColor;
  });

  return nextColorById;
};

const isFiniteNumber = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value);
};

export const toEventDataValue = (event: EventLocationData): Undefinable<number> => {
  const value = (event.data as { value?: unknown } | undefined)?.value;
  return isFiniteNumber(value) ? value : undefined;
};

export const toEventDataIncrease = (event: EventLocationData): Undefinable<number> => {
  const increase = (event.data as { increase?: unknown } | undefined)?.increase;
  return isFiniteNumber(increase) ? increase : undefined;
};

export const toEventDataDuration = (event: EventLocationData): Undefinable<number> => {
  const duration = (event.data as { duration?: unknown } | undefined)?.duration;
  return isFiniteNumber(duration) ? duration : undefined;
};

export const getMostRecentEventBeforeTime = async (
  getLastThingEvent: (entityId: string, params: Record<string, unknown>) => Promise<{ items: unknown[] }>,
  entityId: string,
  target: DateTime,
  feature: string,
): Promise<Undefinable<EventLocationData>> => {
  const response = await getLastThingEvent(entityId, {
    filter: {
      eventTypeId: 'update',
      feature,
      occurred: { $lt: target.toJSDate() },
    },
    limit: 1,
    sort: '-occurred',
  });

  return response.items[0] as EventLocationData | undefined;
};

export const getMostRecentEventAfterTime = async (
  getLastThingEvent: (entityId: string, params: Record<string, unknown>) => Promise<{ items: unknown[] }>,
  entityId: string,
  target: DateTime,
  feature: string,
): Promise<Undefinable<EventLocationData>> => {
  const response = await getLastThingEvent(entityId, {
    filter: {
      eventTypeId: 'update',
      feature,
      occurred: { $gte: target.toJSDate() },
    },
    limit: 1,
    sort: 'occurred',
  });

  return response.items[0] as EventLocationData | undefined;
};

export const getNearestEventByTime = (events: EventLocationData[], target: DateTime, bounds?: { end: DateTime; start: DateTime }): Undefinable<EventLocationData> => {
  if (!events.length) return;

  const targetMillis = target.toMillis();
  const scored = events.map((event) => {
    const occurred = toEventTime(event.occurred);
    return {
      delta: Math.abs(occurred.toMillis() - targetMillis),
      event,
      occurredMillis: occurred.toMillis(),
      withinBounds: bounds ? (occurred >= bounds.start && occurred <= bounds.end) : true,
    };
  });

  const inBounds = scored.filter((item) => item.withinBounds);
  const candidates = inBounds.length ? inBounds : scored;

  candidates.sort((first, second) => {
    if (first.delta !== second.delta) return first.delta - second.delta;
    return first.occurredMillis - second.occurredMillis;
  });

  return candidates[0]?.event;
};

export const isGeofenceEventType = (eventTypeId: string): boolean => {
  return eventTypeId === 'geofence-enter' || eventTypeId === 'geofence-leave' || eventTypeId === 'geofence-exit';
};

export const isGeofenceEnterOrLeaveEventType = isGeofenceEventType;

export {
  buildTripEventSummary, buildTripRouteFullAddressLabel,
  buildTripRouteLabel, enrichEventsWithNearestLocationUpdate, filterExcludedTripHistoryEvents, filterVisibleTripTimelineEvents, formatAddressText, formatLatLngText,
  formatSuburbOrLatLngText,
  formatTotalDistanceLabel,
  formatTotalFuelLabel,
  formatTripDistanceLabel,
  formatTripDurationLabel,
  formatTripEngineHoursLabel,
  formatTripFuelEfficiencyLabel,
  formatTripFuelLabel,
  formatTripOdometerLabel,
  getEventColor,
  getEventDisplayLabel,
  getEventLatLng,
  getThingRangeBoundaryEvents,
  getThingRangeMapEvents,
  getTripEventsInRange,
  getTripTimelineProcessedEvents,
  getVisibleTripTimelineEvents, isExcludedTripHistoryEvent,
  isLocationUpdateEvent,
  isSameTripJourneyMapItem,
  isTripHistoryMapItem, isVisibleTripTimelineEvent, resolveAddressText,
  stripTrailingStatusDot,
  toEventLabel,
  toEventTime
};
export type {
  ReverseGeocodeResolver, TripEventSummarizationOptions,
  TripEventSummaryItem, TripLocationEnrichmentOptions, TripSummaryLike,
  TripTimelineProcessedEvent
};

